import { Input, Modal } from "antd";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updatePassword } from "../../../redux/slice/rfpModuleSlice/dashboard";
import { AppDispatch, RootState } from "redux/store";
import Button from "style-guide/component/Button";
import Typography from "style-guide/component/Typography";
import { openNotificationWithIcon } from "utils/utils";

export interface ISecurityValues {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

const INITIAL_VALUES: ISecurityValues = {
  currentPassword: "",
  newPassword: "",
  confirmNewPassword: "",
};

const Security = () => {
  const [showModal, setShowModal] = useState(false);
  const [data, setData] = useState<ISecurityValues>(INITIAL_VALUES);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  const { isLoadingModal } = useSelector((state: RootState) => state.applicationData);

  const handleModal = () => {
    setShowModal(true);
    setError("");
    setData(INITIAL_VALUES);
  };

  const closeModal = () => {
    setShowModal(false);
    setError("");
    setData(INITIAL_VALUES);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError("");
  };

  const validate = () => {
    if (!data.currentPassword || !data.newPassword || !data.confirmNewPassword) {
      setError("All fields are required.");
      return false;
    }
    if (data.newPassword.length < 8) {
      setError("New password must be at least 8 characters.");
      return false;
    }
    if (data.newPassword !== data.confirmNewPassword) {
      setError("New passwords do not match.");
      return false;
    }
    if (data.currentPassword === data.newPassword) {
      setError("New password must be different from current password.");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    setError("");
    try {
      const status = await dispatch(updatePassword(data));
      if (status === 200) {
        openNotificationWithIcon("success", "Password Updated Successfully!");
        closeModal();
      } else {
        setError("Failed to update password. Please try again.");
      }
    } catch (err: unknown) {
      setError("An unexpected error occurred.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSaveDisabled =
    isSubmitting ||
    isLoadingModal ||
    !data.currentPassword.trim() ||
    !data.newPassword.trim() ||
    !data.confirmNewPassword.trim();

  return (
    <div className="flex gap-4 py-6 border-t-2 border-[whitesmoke]">
      <Typography className="w-64" variant="Heading 3" text="Security" />
      <div className="w-[450px] flex flex-col gap-1">
        <Typography variant="Base" text="Password" />
        <Button
          className="w-fit"
          type="Default"
          buttonText="Change Password"
          onClick={handleModal}
        />
      </div>

      {showModal && (
        <Modal
          title="Change Password"
          open={showModal}
          onOk={handleSave}
          onCancel={closeModal}
          footer={null}
          width={"400px"}
          centered
          maskClosable={false}
          destroyOnClose
        >
          {/* Inputs */}
          <div className="flex flex-col gap-3 pb-3">
            <div className="flex flex-col">
              <Typography text="Current Password" variant="Base" />
              <Input.Password
                name="currentPassword"
                placeholder="Current password"
                onChange={handleChange}
                value={data.currentPassword}
                disabled={isSubmitting}
                autoComplete="current-password"
              />
            </div>
            <div className="flex flex-col">
              <Typography text="New Password" variant="Base" />
              <Input.Password
                name="newPassword"
                placeholder="New password"
                onChange={handleChange}
                value={data.newPassword}
                disabled={isSubmitting}
                autoComplete="new-password"
              />
            </div>
            <div className="flex flex-col">
              <Typography text="Repeat New Password" variant="Base" />
              <Input.Password
                name="confirmNewPassword"
                placeholder="Repeat new password"
                onChange={handleChange}
                value={data.confirmNewPassword}
                disabled={isSubmitting}
                autoComplete="new-password"
              />
            </div>
            {error && (
              <Typography text={error} variant="Small" className="text-red-500" />
            )}
          </div>
          {/* Footer Buttons */}
          <div className="flex justify-end gap-2 pb-[20px]">
            <Button
              buttonText="Cancel"
              type={!isSubmitting ? "Default" : "Disabled"}
              onClick={closeModal}
            />
            <Button
              buttonText={isSubmitting ? "Saving..." : "Save"}
              type={!isSaveDisabled ? "Primary" : "Disabled"}
              onClick={!isSaveDisabled ? handleSave : undefined}
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Security;
